import { apiv1 } from '@/adapters';
import type { RoleNode, TeamModel } from '@/adapters/apiv1.g';
import { extractPageModels, extractTeam } from '@/adapters/shared';
import type { Team } from '@/types/core/team';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

/**
 * Profile service handles current user's tenant-scoped resources
 * (teams, roles, permissions the user belongs to)
 */
export const profileService = {
  /**
   * List my permissions in a specific tenant
   * Returns distinct permission names for the current user (used for authorization checks)
   */
  listMyPermissions: async (tenantId: UUID): Promise<string[]> => {
    const res = await apiv1.listMyPermissions(tenantId);
    return res.data ?? [];
  },

  /**
   * List my roles in a specific tenant
   * Returns all roles for the current user (used for authorization checks)
   */
  listMyRoles: async (tenantId: UUID): Promise<RoleNode[]> => {
    const res = await apiv1.listMyRoles(tenantId);
    return res.data ?? [];
  },

  /**
   * List my teams in a specific tenant
   */
  listMyTeams: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Team>> => {
    const apiQuery = toApiQuery(query);
    const res = await apiv1.listMyTeams(tenantId, apiQuery);
    const models = extractPageModels<TeamModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractTeam),
      next,
      prev,
      limit: query?.limit,
    };
  },
};
